import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Eye } from "lucide-react";
import bgimg from "../assets/loginbg.png";
import leftbgimg from "../assets/leftbg.jpg";
import { Link, useNavigate } from "react-router-dom";

import { logo, personsvg, passworlocksvg, eyelocksvg } from "../utils/data";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import api from "../lib/api.ts";

const LoginForm = ({ tabs = ["Seller", "Buyer"], defaultTab = "Buyer" }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleFormSubmit = async (data) => {
    setIsLoading(true);
    setApiError("");
    try {
      const payload = {
        email: data.email,
        password: data.password,
        userType: activeTab,
      };

      const res = await api.post(`/auth/login`, payload);

      if (res.data?.token) {
        const userData = res.data.user;

        // Validate that the selected tab matches the actual user type
        const actualUserType = getUserTypeFromResponse(userData);

        if (activeTab.toLowerCase() !== actualUserType.toLowerCase()) {
          const translatedType =
            translate(`auth.signup.${actualUserType.toLowerCase()}`) ||
            actualUserType;
          const errorMsg = translate("auth.login.wrongTab").replace(
            /{type}/g,
            translatedType
          );
          setApiError(errorMsg);
          setIsLoading(false);
          return;
        }

        // Use the auth context to login
        const loginSuccess = login(res.data.token, userData);
        if (loginSuccess) {
          navigate("/dashboard");
        } else {
          setApiError(translate("auth.login.loginFailed"));
        }
      } else {
        setApiError(translate("auth.login.invalidResponse"));
      }
    } catch (err) {
      console.error("Error response:", err.response?.data);

      let errorMessage = translate("auth.login.loginFailed");

      if (err.response?.data) {
        const errorData = err.response.data;

        // Handle different error response formats
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors) {
          // Handle validation errors array
          if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors.join(", ");
          } else if (typeof errorData.errors === "object") {
            // Handle object with field-specific errors
            const fieldErrors = Object.values(errorData.errors).flat();
            errorMessage = fieldErrors.join(", ");
          }
        } else if (errorData.validation) {
          // Handle validation object
          if (typeof errorData.validation === "object") {
            const validationErrors = Object.values(errorData.validation).flat();
            errorMessage = validationErrors.join(", ");
          }
        } else if (errorData.details) {
          // Handle details array
          if (Array.isArray(errorData.details)) {
            errorMessage = errorData.details.join(", ");
          }
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to determine user type from backend response
  const getUserTypeFromResponse = (userData) => {
    // Check for buyer indicators
    if (userData?.buyer?.id || userData?.buyerType || userData?.buyer_type) {
      return "Buyer";
    }

    // Check for seller indicators
    if (userData?.seller?.id || userData?.sellerType || userData?.seller_type) {
      return "Seller";
    }

    // Check for userType field
    if (userData?.userType) {
      return userData.userType;
    }

    // Default fallback
    return "Unknown";
  };

  const shouldSwapSides = activeTab === "Buyer";

  // Get translated tab labels
  const getTabLabel = (tab) => {
    return translate(`auth.signup.${tab.toLowerCase()}`) || tab;
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
      className='min-h-screen p-2 sm:p-4 flex items-center justify-center bg-cover bg-center'
      style={{ backgroundImage: `url(${bgimg})` }}
    >
      <div className='bg-white/20 backdrop-blur-lg w-full max-w-7xl rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl border-2 sm:border-[3px] border-white relative overflow-visible md:overflow-hidden'>
        {/* Tabs */}
        {tabs.length > 1 && (
          <div
            className={`absolute -top-0 sm:-top-2 md:top-0 ${
              isRTL
                ? "right-1/2 transform translate-x-1/2"
                : "left-1/2 transform -translate-x-1/2"
            } flex ${isRTL ? "flex-row-reverse" : ""} z-50`}
          >
            {(isRTL ? [...tabs].reverse() : tabs).map((tab, index) => {
              const isFirstTab = index === 0;
              const isLastTab = index === tabs.length - 1;
              return (
                <button
                  key={tab}
                  type='button'
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-2 text-[13px] leading-none sm:px-5 sm:py-3 sm:text-base md:h-[100px] md:w-[200px] md:text-[32px] font-medium transition-all duration-300 cursor-pointer border-2 select-none whitespace-nowrap ${
                    isRTL
                      ? isFirstTab
                        ? "rounded-r-xl sm:rounded-r-2xl"
                        : isLastTab
                        ? "rounded-l-xl sm:rounded-l-2xl"
                        : ""
                      : isFirstTab
                      ? "rounded-l-xl sm:rounded-l-2xl"
                      : isLastTab
                      ? "rounded-r-xl sm:rounded-r-2xl"
                      : ""
                  } ${
                    activeTab === tab
                      ? "bg-[#E55A2B] text-white shadow-lg border-[#E55A2B]"
                      : "bg-white/90 text-gray-700 border-gray-300 hover:bg-[#E55A2B] hover:text-white hover:border-[#E55A2B] hover:shadow-lg"
                  }`}
                  style={{
                    minHeight: "44px",
                    minWidth: "96px",
                  }}
                >
                  {getTabLabel(tab)}
                </button>
              );
            })}
          </div>
        )}

        {/* Tab Selection Warning */}
        {apiError &&
          (apiError.includes("Please switch to the") ||
            apiError.includes("يرجى التبديل")) && (
            <div
              className={`absolute top-[120px] ${
                isRTL
                  ? "right-1/2 transform translate-x-1/2"
                  : "left-1/2 transform -translate-x-1/2"
              } z-40`}
            >
              <div className='bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-2 rounded-lg text-sm font-medium shadow-lg'>
                {translate("auth.login.selectCorrectTab")}
              </div>
            </div>
          )}

        <div
          className={`flex flex-col md:flex-row min-h-[600px] sm:min-h-[700px] md:min-h-[800px] relative pt-3 sm:pt-6 md:pt-0 ${
            shouldSwapSides ? "md:flex-row-reverse" : ""
          }`}
        >
          {/* Left side */}
          <div className='md:w-1/2 relative text-white p-4 sm:p-6 md:p-8 lg:p-10 overflow-hidden min-h-[280px] sm:min-h-[320px] md:min-h-auto'>
            <div
              className='absolute inset-0 bg-cover bg-center'
              style={{ backgroundImage: `url(${leftbgimg})` }}
            >
              <div className='absolute inset-0 bg-black opacity-20'></div>
            </div>
            <div className='relative z-10 text-center pt-8 sm:pt-12 md:pt-20 pb-20 sm:pb-24 md:pb-6'>
              <h2 className='text-xl sm:text-2xl md:text-3xl lg:text-[60px] font-semibold mb-3 sm:mb-4'>
                {translate("auth.login.welcomeBack")}
              </h2>
              <p className='text-xs sm:text-sm md:text-base lg:text-[32px] text-white/90 leading-tight sm:leading-normal'>
                {translate("auth.login.signInAs")} {getTabLabel(activeTab)}
              </p>
            </div>
            <div className='absolute bottom-2 sm:bottom-4 md:bottom-6 flex justify-center items-center z-10 w-full'>
              <div className='scale-75 sm:scale-90 md:scale-100'>{logo}</div>
            </div>
          </div>

          {/* Right side */}
          <div className='md:w-1/2 bg-white/70 backdrop-blur-md p-4 sm:p-6 md:p-8 flex items-center justify-center overflow-y-auto max-h-[600px] sm:max-h-[700px] md:max-h-[800px]'>
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className='w-full max-w-md'
            >
              <div className='space-y-2 sm:space-y-3'>
                <h3 className='text-lg sm:text-2xl md:text-[28px] font-bold mb-1 text-[#131313]'>
                  {translate("auth.login.title")}
                </h3>
                <p className='text-[#13131399] pb-4 sm:pb-8 md:pb-10 text-xs sm:text-sm md:text-[16px]'>
                  {translate("auth.login.subtitle")}
                </p>
              </div>

              {/* Email */}
              <div className='mb-3 sm:mb-4'>
                <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
                  {translate("auth.login.emailLabel")}
                </label>
                <div className='relative'>
                  <div
                    className={`absolute ${
                      isRTL ? "right-2 sm:right-3" : "left-2 sm:left-3"
                    } top-2.5 sm:top-3 scale-90 sm:scale-100`}
                  >
                    {personsvg}
                  </div>
                  <input
                    type='text'
                    placeholder={translate("auth.login.emailPlaceholder")}
                    className={`w-full ${
                      isRTL
                        ? "pr-9 sm:pr-10 pl-3 sm:pl-4"
                        : "pl-9 sm:pl-10 pr-3 sm:pr-4"
                    } py-2.5 sm:py-3 text-[13px] sm:text-base border ${
                      errors.email ? "border-red-500" : "border-[#13131399]"
                    } rounded-lg bg-white placeholder:text-text-[rgba(19,19,19,1)] focus:outline-none `}
                    {...register("email", {
                      required: translate("auth.login.emailRequired"),
                    })}
                  />
                </div>
                {errors.email && (
                  <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className='mb-3 sm:mb-4'>
                <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
                  {translate("auth.login.passwordLabel")}
                </label>
                <div className='relative'>
                  <div
                    className={`absolute ${
                      isRTL ? "right-2 sm:right-3" : "left-2 sm:left-3"
                    } top-2.5 sm:top-3 scale-90 sm:scale-100`}
                  >
                    {passworlocksvg}
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={translate("auth.login.passwordPlaceholder")}
                    className={`w-full ${
                      isRTL
                        ? "pr-9 sm:pr-10 pl-10 sm:pl-12"
                        : "pl-9 sm:pl-10 pr-10 sm:pr-12"
                    } py-2.5 sm:py-3 text-[13px] sm:text-base border ${
                      errors.password ? "border-red-500" : "border-[#13131399]"
                    } rounded-lg bg-white placeholder:text-text-[rgba(19,19,19,1)] focus:outline-none `}
                    {...register("password", {
                      required: translate("auth.login.passwordRequired"),
                    })}
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${
                      isRTL ? "left-3 sm:left-4" : "right-3 sm:right-4"
                    } top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                  >
                    {showPassword ? (
                      <Eye className='w-4 h-4 sm:w-5 sm:h-5' />
                    ) : (
                      <div className='scale-90 sm:scale-100'>{eyelocksvg}</div>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* API Error */}
              {apiError && (
                <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-lg'>
                  <div className='flex items-start'>
                    <div className='flex-shrink-0'>
                      <svg
                        className='h-5 w-5 text-red-400'
                        viewBox='0 0 20 20'
                        fill='currentColor'
                      >
                        <path
                          fillRule='evenodd'
                          d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </div>
                    <div className='ml-3'>
                      <p className='mt-1 text-sm text-red-700'>{apiError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Remember / Forgot */}
              <div
                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-4 sm:mb-6 ${
                  isRTL ? "sm:flex-row-reverse" : ""
                }`}
              >
                <div className='flex items-center'>
                  <input
                    id='remember'
                    type='checkbox'
                    className='w-4 h-4 sm:w-4 sm:h-4 text-orange-600 border-[#13131399] rounded'
                    {...register("remember")}
                  />
                  <label
                    htmlFor='remember'
                    className={`${
                      isRTL ? "mr-2 sm:mr-2" : "ml-2 sm:ml-2"
                    } text-[13px] sm:text-sm text-gray-600`}
                  >
                    {translate("auth.login.rememberMe")}
                  </label>
                </div>
                <button
                  type='button'
                  onClick={() => navigate("/forgot-password")}
                  className={`text-[13px] sm:text-sm md:text-[14px] text-[rgba(19,19,19,1)] hover:text-orange-600 hover:underline ${
                    isRTL
                      ? "text-right sm:text-left"
                      : "text-left sm:text-right"
                  } leading-tight`}
                >
                  {translate("auth.login.forgotPassword")}
                </button>
              </div>

              {/* Submit button */}
              <button
                type='submit'
                disabled={isLoading}
                className={`w-full border border-[#F15A29] bg-white text-[#F15A29] py-2.5 sm:py-3 rounded-lg hover:bg-[#F15A29] hover:text-white transition-all duration-200 font-medium mb-3 sm:mb-4 text-[14px] sm:text-base ${
                  isLoading ? "opacity-90 cursor-not-allowed" : ""
                }`}
              >
                {isLoading
                  ? translate("auth.login.loggingIn")
                  : translate("auth.login.loginButton")}
              </button>

              {/* Switch to register */}
              <p className='text-xs sm:text-sm text-center text-gray-600'>
                {translate("auth.login.notRegistered")}{" "}
                <Link
                  to='/register'
                  state={{ selectedTab: activeTab }}
                  className='text-orange-600 hover:text-orange-700 hover:underline font-medium'
                >
                  {translate("auth.login.registerNow")}
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
